import { OrderMetadataFields } from '@/types/enums/order'
import { TraderViewStatus } from '@/types/enums/trader-view-status'
import {
  isActionRequired,
  isActiveStatus,
  isTerminalStatus,
} from '@/utils/trade-blotter/compute-order-status'

export enum OrderStatusGroup {
  All = 'All',
  ActionRequired = 'Action Required',
  Active = 'Active',
  Completed = 'Completed',
  Rejected = 'Rejected',
}

export const OrderStatusGroupMapping: Record<
  OrderStatusGroup,
  TraderViewStatus[]
> = {
  [OrderStatusGroup.All]: Object.values(TraderViewStatus),
  [OrderStatusGroup.Active]: Object.values(TraderViewStatus).filter((status) =>
    isActiveStatus(status),
  ),
  // Action Required is a subset of Active statuses
  // In trade blotter, Action Required is displayed in a separate group above the rest of the Active statuses
  [OrderStatusGroup.ActionRequired]: Object.values(TraderViewStatus).filter(
    (status) => isActionRequired(status),
  ),
  // Rejected is terminal but surfaced in its own top-level tab, so it is
  // intentionally excluded from the Completed group here.
  [OrderStatusGroup.Completed]: Object.values(TraderViewStatus).filter(
    (status) =>
      isTerminalStatus(status) && status !== TraderViewStatus.Rejected,
  ),
  [OrderStatusGroup.Rejected]: [TraderViewStatus.Rejected],
}

export enum TradeDeskTradeBlotterColumnId {
  ACCOUNT = 'account',
  ACCRUED_INTEREST = 'accrued_interest',
  ADVISOR_EMAIL = 'advisor_email',
  ADVISOR_NAME = 'advisor_name',
  ALLOCATION_STATUS = 'allocation_status',
  AMOUNT = 'amount',
  ANNOTATION = 'annotation',
  ASK_PRICE = 'ask_price',
  ASK_PRICE_ASOF = 'ask_price_asof',
  ASSET_TYPE = 'asset_type',
  BID_PRICE = 'bid_price',
  BID_PRICE_ASOF = 'bid_price_asof',
  BOND_TYPE = 'bond_type',
  CANCEL_REASON = 'cancel_reason',
  CLIENT_ORDER_ID = 'client_order_id',
  COUNTERPARTY = 'counterparty',
  CUSIP = 'cusip',
  CUSTODIAN = 'custodian',
  DAY_RANGE = 'day_range',
  DESCRIPTION_SHORT = 'description_short',
  DESCRIPTION = 'description',
  EXECUTION = 'execution',
  EXTENDED_HOURS = 'extended_hours',
  FILLED = 'filled',
  FILLED_AVG_PRICE = 'filled_avg_price',
  GPO_STATUS = 'gpo_status',
  IB_NAME = 'ib_name',
  ICON_ANNOTATION = 'icon_annotation',
  ID = 'id',
  ISIN = 'isin',
  HANDLING_STATUS = 'handling_status',
  HAS_DISCRETIONARY_AUTHORITY = 'has_discretionary_authority',
  LAST_PRICE = 'last_price',
  LAST_PRICE_ASOF = 'last_price_asof',
  LIMIT_PRICE = 'limit_price',
  LIST_RFQ_NAME = 'list_rfq_name',
  NET_MONEY = 'net_money',
  NOTIONAL_VALUE = 'notional_value',
  PERCENT_CHANGE_1D = 'percent_change_1d',
  PRE_FEE_NET_MONEY = 'pre_fee_net_money',
  ORDER_PROGRESS = 'order_progress',
  PRIME_BROKERAGE_ENABLED = 'prime_brokerage_enabled',
  QUANTITY = 'quantity',
  REJECT_REASON = 'reject_reason',
  ROUTED = 'routed',
  SETTLEMENT_DATE = 'settlement_date',
  SIDE = 'side',
  STATUS = 'traderViewStatus',
  SUBFIRM = 'subfirm',
  PLATFORM = 'platform',
  SUBMISSION_NAME = 'submission_name',
  TIME = 'time',
  TIME_LEFT = 'time_left',
  // biome-ignore lint/style/useLiteralEnumMembers: Bulk suppression as part of biome migration
  TRADER_ID = OrderMetadataFields.TraderId,
  TYPE = 'type',
  UNROUTED = 'unrouted',
  VENUE = 'venue',
  WORKING = 'working',
  YTW = 'ytw',
}
